import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { promptsApi, type PromptPreviewResponse } from '../api/client';
import {
  PROMPT_WORKFLOWS,
  findWorkflowByKey,
  findWorkflowByPrompt,
  normalizeWorkflowType,
} from '../utils/promptWorkflows';
import { findForbiddenWords } from '../utils/forbiddenWords';
import { formatPhraseHits, summarizePreviewChecks, type FailedCheck } from '../utils/promptChecks';
import { useFormDraft, useBeforeUnloadWarning } from '../hooks/useFormDraft';

const isNew = (id: string | undefined) => id === 'new' || !id;

function toDateTimeLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
}

function fromApiDateTime(value: string): string {
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value.slice(0, 16);
  return toDateTimeLocal(new Date(ms));
}

function toApiDateTime(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const ms = Date.parse(trimmed);
  if (Number.isNaN(ms)) return undefined;
  return new Date(ms).toISOString();
}

/** ADM-S02 プロンプト編集・新規作成（API 連携） */
export default function AdminPromptEdit() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(!isNew(id));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * 試し打ち（課題 #58）。保存せずに生成文を確認する。
   * 文面を直した結果を見るのに実際の録音と翌日まで待つしかない状態を解消するもの。
   */
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PromptPreviewResponse | null>(null);
  /** 試し打ちした時点の用途。後から用途の選択を変えても、検査結果の読み替えがずれないように */
  const [previewWorkflowKey, setPreviewWorkflowKey] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [reason, setReason] = useState('');
  const [version, setVersion] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  /**
   * ユーザーが選んだワークフロー種別。
   * 2026-05-30: BE が workflow_type に対応したため、保存ペイロードへ含めて送信する。
   * 既存プロンプトの読み込み時は findWorkflowByPrompt が workflow_type を優先し、
   * 値が無い旧データには title 命名規則による fallback で初期値を埋める
   */
  const [workflowKey, setWorkflowKey] = useState<string>('');
  const effectiveFromMs = effectiveFrom ? Date.parse(effectiveFrom) : Number.NaN;
  const isScheduledFuture = Number.isFinite(effectiveFromMs) && effectiveFromMs > Date.now();

  // ドラフト保護（2026-05-31）: refresh 失敗 / 別タブからのログアウト伝搬 / ブラウザリロード等で
  // 未保存の編集内容が消失するのを防ぐ。sessionStorage にデバウンス保存し、マウント時に復元する。
  const draftKey = `admin/prompt-edit/${isNew(id) ? 'new' : id}`;
  const draftValue = useMemo(
    () => ({ title, content, reason, version, effectiveFrom, workflowKey }),
    [title, content, reason, version, effectiveFrom, workflowKey]
  );
  const { clearDraft } = useFormDraft(draftKey, draftValue, (d) => {
    if (typeof d?.title === 'string') setTitle(d.title);
    if (typeof d?.content === 'string') setContent(d.content);
    if (typeof d?.reason === 'string') setReason(d.reason);
    if (typeof d?.version === 'string') setVersion(d.version);
    if (typeof d?.effectiveFrom === 'string') setEffectiveFrom(d.effectiveFrom);
    // 以前の下書きには旧い値（daily_report_comment 等）が残っていることがある
    if (typeof d?.workflowKey === 'string') setWorkflowKey(normalizeWorkflowType(d.workflowKey) ?? '');
  }, { enabled: !loading });
  // 編集中（title / content / reason のいずれかが入っている）はブラウザ離脱を警告する。
  const isDirty = Boolean(title.trim() || content.trim() || reason.trim());
  useBeforeUnloadWarning(isDirty && !saving);

  /** 直前に表示していたプロンプト。初回表示かどうかの判定に使う */
  const prevIdRef = useRef<string | null>(null);
  useEffect(() => {
    const firstRender = prevIdRef.current === null;
    prevIdRef.current = id ?? 'new';
    if (isNew(id)) {
      setLoading(false);
      // 別のプロンプトの編集から新規作成へ移ったときだけ、入力を空に戻す。
      // 初回表示で空に戻すと、直前に復元した下書きを消してしまう
      // （新規作成の下書きが再読み込み後に戻らなかった原因）
      if (!firstRender) {
        setTitle('');
        setContent('');
        setReason('');
        setVersion('');
        setEffectiveFrom('');
        setWorkflowKey('');
      }
      return;
    }
    const token = getToken();
    if (!token || !id) return;
    setError(null);
    setLoading(true);
    promptsApi
      .get(id, token)
      .then((data) => {
        setTitle(data.title ?? '');
        setContent(data.content ?? '');
        setReason('');
        setVersion(data.version ?? '');
        setEffectiveFrom(data.effective_from ? fromApiDateTime(data.effective_from) : '');
        // 2026-05-30〜: BE が workflow_type を返す場合はそれを採用。
        // 旧データで未設定なら title の命名規則による fallback で逆引きする
        const detected = findWorkflowByPrompt(data);
        setWorkflowKey(detected?.key ?? '');
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
      })
      .finally(() => setLoading(false));
  }, [id, getToken, t]);

  const selectedWorkflow = useMemo(() => findWorkflowByKey(workflowKey), [workflowKey]);

  /** 試し打ちの検査結果を、用途ごとの本番の扱いに沿って並べ直したもの（課題 #62） */
  const checkSummary = useMemo(
    () => (preview?.checks ? summarizePreviewChecks(preview.checks, previewWorkflowKey) : null),
    [preview, previewWorkflowKey]
  );
  const checkKindLabel = checkSummary?.policy
    ? t(`admin.promptPreview.checks.kind.${checkSummary.policy.workflowType}`)
    : '';
  /**
   * BE が締めの一文の付け直し（PR #116）に対応しているか。応答に項目があるかで見分ける。
   * 対応していれば、プロンプトで指定した締めの一文は本番でも必ず文末に付く。
   */
  const closingSupported = preview?.closing_sentence_added !== undefined;
  const checkItemText = (f: FailedCheck): string => {
    switch (f.key) {
      case 'nonDiagnosticMissing':
        return closingSupported
          ? t('admin.promptPreview.checks.nonDiagnosticMissingClosing')
          : t('admin.promptPreview.checks.nonDiagnosticMissing');
      case 'contradictsMcs':
        return t('admin.promptPreview.checks.contradictsMcs');
      case 'leaksInternal':
        return t('admin.promptPreview.checks.leaksInternal', { items: f.items.join('、') });
      case 'prohibitedClaims':
        return t('admin.promptPreview.checks.prohibitedClaims', { items: f.items.join('、') });
      case 'comparisonExpressions':
        return t('admin.promptPreview.checks.comparisonExpressions', { items: f.items.join('、') });
      case 'phraseRules':
        return t('admin.promptPreview.checks.phraseRules', { items: formatPhraseHits(f.items) });
    }
  };

  /**
   * 新規作成時のみ: ワークフローを選んだタイミングで、title が未入力ならタイトル候補を差し込む
   * ユーザーが title を手入力していたら上書きしない（意図を尊重）
   */
  const handleWorkflowChange = (key: string) => {
    setWorkflowKey(key);
    if (!isNew(id)) return;
    const wf = findWorkflowByKey(key);
    if (wf && !title.trim()) {
      setTitle(wf.titleSuggestion);
    }
  };

  /**
   * 編集中の本文で試し打ちする（課題 #58）。保存はしない。
   * 入力データは既定の synthetic（固定の合成データ）を使う。
   * 実在の解析値を使うと、文面を変えたときの差分に入力側の揺れが混ざり、
   * 「文面を直した効果」が読めなくなるため。
   */
  const handlePreview = useCallback(() => {
    const token = getToken();
    if (!token || !content.trim() || previewing) return;
    setPreviewError(null);
    setPreview(null);
    setPreviewing(true);
    promptsApi
      .preview({ content, workflow_type: workflowKey || null }, token)
      .then((data) => {
        setPreview(data);
        setPreviewWorkflowKey(workflowKey);
      })
      .catch((err) => setPreviewError(err instanceof Error ? err.message : t('common.error')))
      .finally(() => setPreviewing(false));
  }, [getToken, content, workflowKey, previewing, t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    setSaveError(null);
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setSaveError(t('admin.promptEditReasonRequired'));
      return;
    }
    setSaving(true);
    // 2026-05-30〜: BE が workflow_type を受け入れる。
    // 未選択（空文字）の場合は undefined にして送信ペイロードから除外する
    const workflowType = workflowKey || undefined;
    if (isNew(id)) {
      promptsApi
        .create(
          {
            title: title.trim(),
            content: content.trim(),
            reason: trimmedReason,
            version: version.trim() || undefined,
            effective_from: toApiDateTime(effectiveFrom),
            workflow_type: workflowType,
          },
          token
        )
        .then(() => {
          clearDraft();
          navigate('/admin/prompts');
        })
        .catch((err) => {
          setSaveError(err instanceof Error ? err.message : t('common.error'));
          setSaving(false);
        });
    } else if (id) {
      promptsApi
        .update(
          id,
          {
            reason: trimmedReason,
            title: title.trim() || undefined,
            content: content.trim() || undefined,
            version: version.trim() || undefined,
            effective_from: toApiDateTime(effectiveFrom),
            workflow_type: workflowType,
          },
          token
        )
        .then(() => {
          clearDraft();
          navigate('/admin/prompts');
        })
        .catch((err) => {
          setSaveError(err instanceof Error ? err.message : t('common.error'));
          setSaving(false);
        });
    }
  };

  if (loading) {
    return (
      <div className="page admin-prompt-edit-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="page admin-prompt-edit-page">
      <h1>{t('admin.prompts')} — {isNew(id) ? t('admin.promptEditNew') : t('admin.promptEditEdit')}</h1>
      {!isNew(id) && <p className="admin-prompt-id">ID: {id ?? '—'}</p>}

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <p className="admin-back">
            <Link to="/admin/prompts">{t('common.back')} → {t('admin.promptBackToList')}</Link>
          </p>
        </div>
      )}

      {!error && (
        <form onSubmit={handleSubmit} className="admin-card admin-form">
          <div className="admin-form-group">
            <label htmlFor="admin-promptWorkflow-fieldLabel">{t('admin.promptWorkflow.fieldLabel')}</label>
            <select id="admin-promptWorkflow-fieldLabel"
              value={workflowKey}
              onChange={(e) => handleWorkflowChange(e.target.value)}
            >
              <option value="">{t('admin.promptWorkflow.noneOption')}</option>
              {PROMPT_WORKFLOWS.map((wf) => (
                <option key={wf.key} value={wf.key}>
                  {wf.label}
                </option>
              ))}
            </select>
            {selectedWorkflow ? (
              <div className="admin-prompt-workflow-info">
                <p>{selectedWorkflow.description}</p>
                <p className="admin-form-hint">
                  {t('admin.promptWorkflow.triggeredBy', { value: selectedWorkflow.triggeredBy })}
                </p>
                <p className="admin-form-hint">
                  {t('admin.promptWorkflow.titleSuggestion', { value: selectedWorkflow.titleSuggestion })}
                </p>
              </div>
            ) : (
              <p className="admin-form-hint">{t('admin.promptWorkflow.noneHint')}</p>
            )}
          </div>
          <div className="admin-form-group">
            <label htmlFor="admin-promptEditTitle">{t('admin.promptEditTitle')}</label>
            <input id="admin-promptEditTitle" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={255} />
          </div>
          <div className="admin-form-group">
            <label htmlFor="admin-promptEditVersion">{t('admin.promptEditVersion')}</label>
            <input id="admin-promptEditVersion" type="text" value={version} onChange={(e) => setVersion(e.target.value)} maxLength={50} placeholder={t('admin.promptEditVersionPlaceholder')} />
          </div>
          <div className="admin-form-group">
            <label htmlFor="admin-promptEditEffectiveFrom">{t('admin.promptEditEffectiveFrom')}</label>
            <input id="admin-promptEditEffectiveFrom" type="datetime-local" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={() => setEffectiveFrom(toDateTimeLocal(new Date()))}
            >
              {t('admin.promptEditSetNow')}
            </button>
            {isScheduledFuture && (
              <p className="admin-form-warning">{t('admin.promptEditFutureWarning')}</p>
            )}
          </div>
          <div className="admin-form-group">
            <label htmlFor="admin-promptEditContent">{t('admin.promptEditContent')}</label>
            <textarea id="admin-promptEditContent" rows={10} value={content} onChange={(e) => setContent(e.target.value)} required className="admin-textarea" />
            {(() => {
              const hits = findForbiddenWords(content);
              if (hits.length === 0) return null;
              return (
                <div className="admin-form-warning" role="status">
                  <p>
                    <strong>{t('admin.consentDocs.forbiddenWordsWarning.title')}</strong>
                  </p>
                  <ul className="admin-consent-docs-forbidden-list">
                    {hits.map((h) => (
                      <li key={h.word}>
                        「{h.word}」 — {t('admin.consentDocs.forbiddenWordsWarning.snippet', { snippet: h.snippet })}
                      </li>
                    ))}
                  </ul>
                  <p className="admin-form-hint">
                    {t('admin.consentDocs.forbiddenWordsWarning.note')}
                  </p>
                </div>
              );
            })()}
          </div>
          <div className="admin-form-group">
            <label htmlFor="admin-promptEditReason">{t('admin.promptEditReason')}</label>
            <input id="admin-promptEditReason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              maxLength={255}
              placeholder={t('admin.promptEditReasonPlaceholder')}
            />
          </div>
          {/*
            試し打ち（課題 #58）。保存前に生成文を確認する。
            これが無いと、文面を 1 語直すたびに実際の録音と翌日の待ちが必要だった。
            type="button" にしないと保存が走ってしまう点に注意。
          */}
          <div className="admin-form-group admin-prompt-preview">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={handlePreview}
              disabled={previewing || !content.trim()}
              data-testid="prompt-preview-run"
            >
              {previewing ? t('admin.promptPreview.running') : t('admin.promptPreview.run')}
            </button>
            <p className="admin-form-hint">{t('admin.promptPreview.hint')}</p>

            {previewError && (
              <p className="admin-form-error" data-testid="prompt-preview-error">
                {previewError}
              </p>
            )}

            {preview && (
              <div className="admin-prompt-preview-result" data-testid="prompt-preview-result">
                {/*
                  REG-01 で置換された場合、書いた文面のとおりには出ていない。
                  黙って結果だけ見せると「書いたとおりに出る」と誤解されるため明示する。
                */}
                {preview.sanitized && (
                  <p className="admin-form-warning" data-testid="prompt-preview-sanitized">
                    {t('admin.promptPreview.sanitized')}
                  </p>
                )}
                {/*
                  生成後の検査結果（課題 #62）。登録前に一番知るべきことなので、生成文より上に出す。
                  試し打ちは用途にかかわらず 4 項目を検査するが、本番で行う検査は用途ごとに違う。
                  本番で起きないことを「起きる」と言わないよう、本番でも行う検査と参考の検査を分ける。
                */}
                {checkSummary && (
                  <div
                    className={`admin-prompt-checks ${checkSummary.outcome === 'pass' ? 'is-passed' : 'is-failed'}`}
                    data-testid="prompt-preview-checks"
                  >
                    <p className="admin-prompt-checks-title">
                      {checkSummary.failedInProduction.length === 0 &&
                      checkSummary.failedPreviewOnly.length === 0
                        ? t('admin.promptPreview.checks.allPassed')
                        : !checkSummary.policy
                        ? t('admin.promptPreview.checks.failedUnknownKind')
                        : checkSummary.outcome === 'pass'
                        ? t('admin.promptPreview.checks.productionPassed', { kind: checkKindLabel })
                        : t('admin.promptPreview.checks.productionFailed', { kind: checkKindLabel })}
                    </p>
                    {checkSummary.failedInProduction.length > 0 && (
                      <ul className="admin-prompt-checks-list" data-testid="prompt-preview-checks-production">
                        {checkSummary.failedInProduction.map((f) => (
                          <li key={f.key}>{checkItemText(f)}</li>
                        ))}
                      </ul>
                    )}
                    {checkSummary.outcome === 'retry' && (
                      <p className="admin-form-hint" data-testid="prompt-preview-would-retry">
                        {t('admin.promptPreview.checks.wouldRetry')}
                      </p>
                    )}
                    {checkSummary.outcome === 'template' && (
                      <p className="admin-form-hint" data-testid="prompt-preview-would-template">
                        {t('admin.promptPreview.checks.wouldTemplate')}
                      </p>
                    )}
                    {checkSummary.failedPreviewOnly.length > 0 && (
                      <>
                        <p className="admin-prompt-checks-subtitle">
                          {t('admin.promptPreview.checks.previewOnlyTitle', { kind: checkKindLabel })}
                        </p>
                        <ul className="admin-prompt-checks-list" data-testid="prompt-preview-checks-reference">
                          {checkSummary.failedPreviewOnly.map((f) => (
                            <li key={f.key}>{checkItemText(f)}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {/* 「記録だけ」の文字列（課題 #62 / BE PR #170）。本番の文面は変えず件数だけを数えるので、合否とは分けて出す */}
                    {checkSummary.recordedPhrases.length > 0 && (
                      <p className="admin-form-hint" data-testid="prompt-preview-phrase-records">
                        {t('admin.promptPreview.checks.phraseRecords', { items: formatPhraseHits(checkSummary.recordedPhrases) })}
                      </p>
                    )}
                    {checkSummary.policy && (
                      <p className="admin-form-hint" data-testid="prompt-preview-production-scope">
                        {checkSummary.policy.checks.length > 0
                          ? t('admin.promptPreview.checks.scope', {
                              kind: checkKindLabel,
                              items: checkSummary.policy.checks
                                .map((k) => t(`admin.promptPreview.checks.name.${k}`))
                                .join('・'),
                            })
                          : t('admin.promptPreview.checks.scopeNone', { kind: checkKindLabel })}
                      </p>
                    )}
                    {/* 締めの一文の付け直しは日次・週次・月次のみ（BE PR #116） */}
                    {closingSupported &&
                      checkSummary.policy &&
                      checkSummary.policy.workflowType !== 'health_management' && (
                        <p className="admin-form-hint" data-testid="prompt-preview-closing-guarantee">
                          {t('admin.promptPreview.checks.closingGuarantee')}
                        </p>
                      )}
                  </div>
                )}
                {/*
                  モデルが締めの一文を指定どおりに書かなかった（コードが付け直した）。
                  文面は本番でも同じに整うが、指示の効き具合を確かめる手がかりになる（BE の依頼）
                */}
                {preview.closing_sentence_added === true && (
                  <p className="admin-form-hint" data-testid="prompt-preview-closing-added">
                    {t('admin.promptPreview.checks.closingAdded')}
                  </p>
                )}
                <p className="admin-form-hint">
                  {t('admin.promptPreview.meta', {
                    sample: preview.sample_kind,
                    seconds: (preview.elapsed_ms / 1000).toFixed(1),
                  })}
                </p>
                <pre className="admin-prompt-preview-text">
                  {preview.generated_text || t('admin.promptPreview.empty')}
                </pre>
              </div>
            )}
          </div>

          {saveError && <p className="admin-form-error">{saveError}</p>}
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? t('common.loading') : t('admin.promptEditSave')}
          </button>
        </form>
      )}

      <p className="admin-back">
        <Link to="/admin/prompts">{t('common.back')} → {t('admin.promptBackToList')}</Link>
      </p>
    </div>
  );
}
